import type { Feature, Point } from "geojson";

import {
  appendBuildings,
  type BuildingElement,
  BuildingState,
  type HistoryElement,
  HistoryType,
  pushElementToHistory,
  setBuilding,
  setBuildings,
  setCurrentBuildingID,
  setCurrentFilterText,
  setCurrentFloorID,
  setCurrentPoiCategoriesIDs,
  setCurrentPoiID,
  unsetCurrentPoiID,
} from "@/features/cartography/cartographySlice";
import { setFollowUser } from "../map/mapSlice";

import { selectCurrentBuilding } from "@/features/cartography/cartographySlice.selectors";
import { buildingMapper, imdfMapper } from "@/libs/factories";
import {
  type Building,
  FIND_MY_CAR_LOCATION_ID,
  type Floor,
  type Poi,
  type PoiCategory,
} from "@/libs/models";
import store from "../../app/store";
import { selectBuildingForFindMyCar } from "../find_my_car/actions";
import { resetViewState } from "../map/actions";
import type { SitumAPIContextType } from "../situm_api/SitumAPIProvider";
import {
  emmitEventPoiCategorySelected,
  emmitEventPoiSelected,
  emmitEventSearched,
} from "../utils/eventEmmiter";

const createBuildingStoreElement = (
  building: Building,
  state: BuildingState = BuildingState.COMPLETE,
  timestamp = Date.now(),
) => ({
  state,
  timestap: timestamp,
  value: building,
});

// Updates buildings
export const appendBuildingsAction = (
  buildings: Building[],
  state: BuildingState = BuildingState.COMPLETE,
) => {
  const timestamp = Date.now();
  store.dispatch(
    appendBuildings(
      buildings.map((b) => createBuildingStoreElement(b, state, timestamp)),
    ),
  );
};

// Resets all building information and adds new buildings
export const setBuildingsAction = (
  buildings: Building[],
  state: BuildingState = BuildingState.COMPLETE,
) => {
  const timestamp = Date.now();
  store.dispatch(
    setBuildings(
      buildings.map((b) => createBuildingStoreElement(b, state, timestamp)),
    ),
  );
};

// Appends new building to current building list
export const setBuildingAction = (
  building: Building,
  state: BuildingState = BuildingState.COMPLETE,
) => {
  store.dispatch(setBuilding(createBuildingStoreElement(building, state)));
};

export const selectBuildingById = (id: number) => {
  return Object.values(store.getState().cartography.buildings).find(
    (b) => b.value.id == id,
  )?.value;
};

export const selectBuildingByFloorId = (floorId: number) => {
  return Object.values(store.getState().cartography.buildings).find((b) =>
    b.value.floors?.find((f: Floor) => f.id == Number(floorId)),
  )?.value;
};

export const handleSetBuilding = (id: number) => {
  if (!id) return;
  const building = selectBuildingById(id);
  if (!building || !building?.id) return;
  store.dispatch(setCurrentBuildingID(building.id));
};

/**
 * It searches for a building that contains a given POI, if the POI is not on the current
 * building it searches on the rest of the buildings by fetching its pois.
 * @param poiId
 * @param situmAPIContext
 * @returns
 */
export const selectBuildingByPoiId = async (
  poiSelector: { poiId: Poi["id"] } | { externalPoiId: string },
  situmAPIContext: SitumAPIContextType,
) => {
  const cartographySlice = store.getState().cartography;

  if ("poiId" in poiSelector && poiSelector.poiId == FIND_MY_CAR_LOCATION_ID) {
    return selectBuildingForFindMyCar();
  }

  const building =
    cartographySlice.currentBuildingID === null
      ? undefined
      : cartographySlice.buildings[cartographySlice.currentBuildingID]?.value;

  const buildingMatchesPoi = (
    building: Building,
    poiSelector: { poiId: Poi["id"] } | { externalPoiId: string },
  ) => {
    if ("poiId" in poiSelector) {
      return building.pois?.getPoiById(poiSelector.poiId);
    } else {
      return building.pois?.findByExternalId(poiSelector.externalPoiId);
    }
  };

  if (!buildingMatchesPoi(building, poiSelector)) {
    for (const b of Object.values(cartographySlice.buildings)) {
      // Retrieve list of pois for the building
      const pois = await situmAPIContext.getPoisOnBuilding({
        id: b.value.id,
      });
      if (buildingMatchesPoi(b.value, poiSelector)) {
        // Update list of pois
        situmAPIContext.setPoisOnBuilding({ id: b.value.id, pois });
        return b.value;
      }
    }
    return undefined;
  }
  return building;
};

/**
 * Selects the closest building to a given geojson point.
 *
 * @param position The geojson point to find the closest building for.
 * @param thresholdMeters The maximum distance to consider for a building as the closest. Default is 5000 meters.
 *
 * @returns The closest building to the given geojson point. If no building is found within the threshold, returns null.
 */
export const selectClosestBuilding = (
  position: Feature<Point>,
  thresholdMeters = 5000,
): { building: Building | null; distance: number } => {
  const buildings = store.getState().cartography.buildings;
  if (!buildings) return { building: null, distance: Infinity };

  return Object.values(buildings).reduce(
    (
      closest: { building: Building | null; distance: number },
      current: BuildingElement,
    ) => {
      const distanceToBuilding = current.value.distanceFrom(position);

      if (
        (closest.building === null || distanceToBuilding < closest.distance) &&
        distanceToBuilding < thresholdMeters
      ) {
        return { building: current.value, distance: distanceToBuilding };
      }

      return closest;
    },
    { building: null, distance: Infinity },
  );
};

/**
 * Fetches a building's information and maps the result to a valid {@link Building} object
 *
 * @param situmAPI
 * @param buildingIdentifier
 */

const unsetBuildingsPoisGeofencesAndPaths = (building: Building): Building => {
  building.paths = undefined;
  building.geofences = undefined;
  building.pois = undefined;
  return building;
};

export const getInitialBuilding = (buildingMap: Map<number, any>) => {
  if (buildingMap === null) {
    return null;
  }

  const buildingIdentifier =
    store.getState().config.map.initialView.building_id;
  const domain = store.getState().config.auth.domain;

  if (buildingIdentifier && buildingMap.has(buildingIdentifier) && domain) {
    return applyBuildingMapper(buildingMap.get(buildingIdentifier));
  }

  return applyBuildingMapper(buildingMap.values().next().value);
};

export const loadIMDf = async (b: Building) => {
  const domain = store.getState().config.auth.domain;
  if (!b?.customFields) return b;

  return imdfMapper(b.customFields, b.floors, domain).then((imdf) => {
    b.setIMDF(imdf);
    return b;
  });
};

export const applyBuildingMapper = (building: any) => {
  const b = buildingMapper(building) as Building;

  if (b == null) return b;

  return unsetBuildingsPoisGeofencesAndPaths(b);
};

/**
 * Extract a custom css inside the buildings' custom fields.
 *
 * @param building
 * @returns
 */
export const getStyleFromCF = (building: Building) => {
  const styleLink =
    building.getCustomFieldValue("map_viewer_custom_style") || "";

  if (!styleLink) return;
  const sheet = document.createElement("link");
  sheet.rel = "stylesheet";
  sheet.href = styleLink;
  sheet.type = "text/css";
  sheet.media = "all";
  document.body.appendChild(sheet);
  return true;
};

export const handleSelectPoi = async (
  poiSelector: { poiId: Poi["id"] } | { externalPoiId: string },
  situmAPIContext: SitumAPIContextType,
) => {
  const navigationTo = store.getState().navigation.navigationTo;
  const findMyCar = store.getState().findMyCar;
  // do not select a POI while in navigation.
  if (navigationTo) {
    console.error(`Currently navigating towards poi with id ${navigationTo}`);
    return;
  }

  const building = await selectBuildingByPoiId(poiSelector, situmAPIContext);

  if (!building) {
    console.error("POI not found");
    return;
  }

  if ("poiId" in poiSelector && poiSelector.poiId == FIND_MY_CAR_LOCATION_ID) {
    // Need to manually set the building because this is a fake poi
    store.dispatch(setCurrentBuildingID(building.id));
    store.dispatch(setCurrentFloorID(findMyCar.floorId));
  }

  store.dispatch(setCurrentFilterText(""));
  store.dispatch(setCurrentPoiCategoriesIDs(null));
  //set current poi
  if ("poiId" in poiSelector) {
    handleSetCurrentPoiID({ poiId: poiSelector.poiId });
  } else {
    const poiID = building.pois.findByExternalId(poiSelector.externalPoiId)?.id;
    handleSetCurrentPoiID({ poiId: poiID });
  }
};

export const handleDeselectPoi = async () => {
  store.dispatch(unsetCurrentPoiID());
};

export const handleSelectFloor = (identifier: number, fitCamera: boolean) => {
  const { followUser } = store.getState().map;
  const currentLocation = store.getState().location.currentLocation;

  store.dispatch(setCurrentFloorID(identifier));
  fitCamera && resetViewState({ disableFollowUser: false });
  followUser && currentLocation && store.dispatch(setFollowUser(false));
};

export const handleAddElementToHistory = (historyElement: HistoryElement) => {
  const buildingIdentifier = store.getState().cartography.currentBuildingID;
  historyElement.type !== HistoryType.POI_CLICK &&
    emmitEventSearched({
      buildingId: buildingIdentifier,
      term: historyElement.text,
    });
  store.dispatch(pushElementToHistory(historyElement));
};

// add one
export const handleSetCurrentPoiCategory = (
  poiCategorySelector:
    | { poiCategoryId: PoiCategory["id"] }
    | { externalPoiCategoryId: string },
) => {
  const buildingId = store.getState().cartography.currentBuildingID;

  const poiCategoryID =
    "poiCategoryId" in poiCategorySelector
      ? poiCategorySelector.poiCategoryId
      : store
          .getState()
          .app.poiCategories.findByExternalId(
            poiCategorySelector.externalPoiCategoryId,
          )?.id;

  if (!poiCategoryID) {
    console.error("POI category not found");
    return;
  }

  const poiToSet = store
    .getState()
    .app.poiCategories.findCategoryById(poiCategoryID);
  if (!poiToSet) return;

  emmitEventPoiCategorySelected({
    buildingId,
    poiCategoryId: poiToSet.id,
  });

  const subcats = poiToSet?.getAllSubcategoriesIds()?.flatMap((p) => p) || [];
  store.dispatch(setCurrentPoiCategoriesIDs([poiCategoryID, ...subcats]));
};

// add several
export const handleSetCurrentPoiCategories = (
  poiCategoryIDs: PoiCategory["id"][],
) => {
  const state = store.getState();
  const buildingId = state.cartography.currentBuildingID;
  const subcategories = poiCategoryIDs.flatMap((poiCategoryID) => {
    const poiToSet = state.app.poiCategories.findCategoryById(poiCategoryID);
    return poiToSet ? poiToSet.getAllSubcategoriesIds() : [];
  });

  poiCategoryIDs &&
    poiCategoryIDs[0] &&
    emmitEventPoiCategorySelected({
      buildingId,
      poiCategoryId: poiCategoryIDs[0],
    });

  const allCategories = [...poiCategoryIDs, ...subcategories];
  store.dispatch(setCurrentPoiCategoriesIDs(allCategories));
};

// remove one
export const handleRemoveCurrentPoiCategory = (
  poiCategoryID: PoiCategory["id"],
) => {
  const state = store.getState();
  const poiToRemove = state.app.poiCategories.findCategoryById(poiCategoryID);
  if (poiToRemove?.parentCategoryId) {
    handleSetCurrentPoiCategory({
      poiCategoryId: poiToRemove?.parentCategoryId,
    });
    return;
  }
  const subcategoriesToRemove = poiToRemove
    ? poiToRemove.getAllSubcategoriesIds()
    : [];

  const remainingCategories = state.cartography.currentPoiCategoriesIDs
    .filter(
      (id) => id !== poiCategoryID && !subcategoriesToRemove?.includes(id),
    )
    .filter(Boolean);
  store.dispatch(setCurrentPoiCategoriesIDs(remainingCategories));
};

// remove all
export const handleRemoveCurrentPoiCategories = () => {
  store.dispatch(setCurrentPoiCategoriesIDs(null));
};

export const handleSetCurrentPoiID = ({
  poiId,
  emmitEvent = true,
}: {
  poiId: number;
  emmitEvent?: boolean;
}) => {
  emmitEvent && emmitEventPoiSelected(poiId);
  store.dispatch(setCurrentPoiID(poiId));
};

export const isValidFloorId = (floorId?: number) => {
  if (!floorId) return false;

  const building = selectCurrentBuilding(store.getState());
  return !!building.getFloorById(floorId);
};
